import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../utils/currency';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Pets,
  DateRange,
  LocationOn,
  TrendingUp,
  AttachMoney,
  Sell,
  Receipt,
  History,
  ExpandMore,
  Warning,
  Add
} from '@mui/icons-material';

const ViewBatch = ({ open, onClose, batch, onEdit, onSell, onExpenditure }) => {
  const [batchHistory, setBatchHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (batch && batch.id && open) {
      fetchBatchHistory();
    }
  }, [batch, open]);

  const fetchBatchHistory = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/batches/${batch.id}/history`);
      if (response.ok) {
        const history = await response.json();
        setBatchHistory(history);
      } else {
        console.warn('Failed to fetch batch history');
        setBatchHistory([]);
      }
    } catch (error) {
      console.error('Error fetching batch history:', error);
      setBatchHistory([]);
    } finally {
      setLoading(false);
    }
  };

  if (!batch) return null;
  
  // Debug logging to see what data we have
  console.log('ViewBatch - batch data:', batch);
  console.log('ViewBatch - expenditures:', batch.expenditures);
  console.log('ViewBatch - batch history:', batchHistory);
  
  // Fallback: Try to get expenditures from localStorage if backend doesn't have them
  let expenditures = batch.expenditures || [];
  const fallbackKey = `batch_${batch.id}_expenditures`;
  
  if ((!expenditures || expenditures.length === 0) && batch.id) {
    try {
      const storedExpenditures = localStorage.getItem(fallbackKey);
      if (storedExpenditures) {
        expenditures = JSON.parse(storedExpenditures);
      }
    } catch (error) {
      console.warn('Failed to load fallback expenditures:', error);
    }
  }

  // Calculate real financial metrics from expenditures
  const totalExpenditureAmount = expenditures.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
  const realCostPerBird = totalExpenditureAmount / (batch.initial_count || batch.current_count || 1);
  
  // Calculate feed-related expenditures for feed consumption estimate
  const feedExpenditures = expenditures.filter(exp => 
    exp.category === 'Food' || exp.category === 'Feed'
  );
  const totalFeedCost = feedExpenditures.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
  
  // Estimate feed consumption based on actual feed costs
  const estimatedFeedCostPerKg = 1500;
  const totalFeedKg = totalFeedCost / estimatedFeedCostPerKg;
  const ageInDays = (batch.age_in_weeks || batch.age || 1) * 7;
  const dailyFeedConsumption = totalFeedKg / Math.max(ageInDays, 1);

  // Calculate mortality and additions from history
  const mortalityEvents = batchHistory.filter(h => h.change_type === 'mortality');
  const additionEvents = batchHistory.filter(h => h.change_type === 'addition');
  const totalMortality = mortalityEvents.reduce((sum, event) => sum + Math.abs(event.quantity_change), 0);
  const totalAdditions = additionEvents.reduce((sum, event) => sum + event.quantity_change, 0);
  
  const getChangeIcon = (changeType) => {
    switch (changeType) {
      case 'mortality': return '💀';
      case 'addition': return '➕';
      case 'sale': return '💰';
      case 'initial': return '🐣';
      default: return '📝';
    }
  };

  const getChangeColor = (changeType) => {
    switch (changeType) {
      case 'mortality': return 'error';
      case 'addition': return 'success';
      case 'sale': return 'info';
      case 'initial': return 'primary';
      default: return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Pets color="primary" />
          <Typography variant="h6">Batch Details - {batch.batch_code || batch.code}</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Basic Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">Batch Code</Typography>
                  <Typography variant="body1" fontWeight="bold">{batch.batch_code || batch.code}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">Type</Typography>
                  <Chip 
                    label={batch.type} 
                    color={batch.type === 'Layers' ? 'success' : batch.type === 'Broilers' ? 'warning' : 'info'}
                    size="small"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">Initial Count</Typography>
                  <Typography variant="body1" fontWeight="bold">{batch.initial_count} birds</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">Current Count</Typography>
                  <Typography variant="body1" fontWeight="bold">{batch.current_count || batch.count} birds</Typography>
                </Box>
                {/* Show mortality and additions summary */}
                {(totalMortality > 0 || totalAdditions > 0) && (
                  <Box sx={{ mb: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Count Changes Summary:</Typography>
                    {totalAdditions > 0 && (
                      <Typography variant="body2" color="success.main">
                        ➕ Total Additions: +{totalAdditions} birds
                      </Typography>
                    )}
                    {totalMortality > 0 && (
                      <Typography variant="body2" color="error.main">
                        💀 Total Mortality: -{totalMortality} birds
                      </Typography>
                    )}
                  </Box>
                )}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">Age</Typography>
                  <Typography variant="body1">{batch.age_in_weeks || batch.age} weeks</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip 
                    label={batch.status} 
                    color={batch.status === 'Active' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Production Stats */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Production Stats
                </Typography>
                {batch.type === 'Layers' && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">Daily Egg Production</Typography>
                      <Typography variant="body1" fontWeight="bold">~{Math.floor((batch.current_count || batch.count) * 0.8)} eggs/day</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">Weekly Production</Typography>
                      <Typography variant="body1">~{Math.floor((batch.current_count || batch.count) * 0.8 * 7)} eggs/week</Typography>
                    </Box>
                  </>
                )}
                {batch.type === 'Broilers' && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">Average Weight</Typography>
                      <Typography variant="body1" fontWeight="bold">{((batch.age_in_weeks || batch.age) * 0.3).toFixed(1)} kg</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">Market Ready</Typography>
                      <Typography variant="body1">{(batch.age_in_weeks || batch.age) >= 6 ? 'Yes' : `${6 - (batch.age_in_weeks || batch.age)} weeks remaining`}</Typography>
                    </Box>
                  </>
                )}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">Feed Consumption</Typography>
                  <Typography variant="body1">
                    {dailyFeedConsumption > 0 
                      ? `${dailyFeedConsumption.toFixed(1)} kg/day (based on feed costs)`
                      : `${((batch.current_count || batch.count) * 0.12).toFixed(1)} kg/day (estimated)`
                    }
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Financial Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney />
                  Financial Summary
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">Total Initial Investment</Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {totalExpenditureAmount > 0 
                      ? formatCurrency(totalExpenditureAmount)
                      : formatCurrency(batch.total_initial_cost || batch.totalInitialCost)
                    }
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">Cost per Bird</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {totalExpenditureAmount > 0 
                      ? formatCurrency(realCostPerBird)
                      : formatCurrency(batch.cost_per_bird || batch.costPerBird)
                    }
                  </Typography>
                </Box>
                {/* Show expenditure breakdown if available */}
                {((expenditures && expenditures.length > 0) || (batch.total_chicken_cost && parseFloat(batch.total_chicken_cost) > 0)) && (
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Initial Expenditure Breakdown:</Typography>
                    
                    {expenditures && expenditures.map((exp, index) => {
                      const amount = parseFloat(exp.amount.toString().replace(/,/g, '')) || 0;
                      return (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, p: 0.5, backgroundColor: '#fafafa', borderRadius: 0.5 }}>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {exp.category === 'Food' || exp.category === 'Feed' ? '🌾' : 
                               exp.category === 'Medicine' || exp.category === 'Medication' ? '💊' : 
                               exp.category === 'Equipment' ? '🔧' : 
                               exp.category === 'Transportation' ? '🚚' : 
                               exp.category === 'Labor' ? '👷' : 
                               exp.category === 'Vitamins' ? '💊' : '📦'} 
                              {exp.category} - {exp.type}
                            </Typography>
                            {exp.description && (
                              <Typography variant="caption" color="textSecondary">{exp.description}</Typography>
                            )}
                          </Box>
                          <Typography variant="body2" fontWeight="bold" color="secondary">{formatCurrency(amount)}</Typography>
                        </Box>
                      );
                    })}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                      <Typography variant="body2" fontWeight="bold">Total Investment:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {formatCurrency(totalExpenditureAmount || batch.total_initial_cost || batch.totalInitialCost)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Source Information */}
          {(batch.source || batch.breed) && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Source & Breed Information
                  </Typography>
                  {batch.breed && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">Breed</Typography>
                      <Typography variant="body1" fontWeight="bold">{batch.breed}</Typography>
                    </Box>
                  )}
                  {batch.source && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">Source/Supplier</Typography>
                      <Typography variant="body1">{batch.source}</Typography>
                    </Box>
                  )}
                  {batch.notes && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">Notes</Typography>
                      <Typography variant="body2">{batch.notes}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Batch History Section */}
          <Grid item xs={12}>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="batch-history-content"
                id="batch-history-header"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History color="primary" />
                  <Typography variant="h6">Batch History & Count Changes</Typography>
                  {batchHistory.length > 0 && (
                    <Chip 
                      label={`${batchHistory.length} events`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {loading ? (
                  <Typography>Loading history...</Typography>
                ) : batchHistory.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Event</TableCell>
                          <TableCell align="right">Change</TableCell>
                          <TableCell align="right">Previous</TableCell>
                          <TableCell align="right">New Count</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {batchHistory
                          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                          .map((event, index) => (
                          <TableRow key={event.id || index}>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(event.date).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                icon={<span>{getChangeIcon(event.change_type)}</span>}
                                label={event.change_type.charAt(0).toUpperCase() + event.change_type.slice(1)}
                                color={getChangeColor(event.change_type)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography 
                                variant="body2" 
                                fontWeight="bold"
                                color={event.quantity_change > 0 ? 'success.main' : event.quantity_change < 0 ? 'error.main' : 'text.primary'}
                              >
                                {event.quantity_change > 0 ? '+' : ''}{event.quantity_change}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{event.previous_count}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">{event.new_count}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                                {event.notes}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <History sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      No history available
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Count changes and events will appear here when recorded.
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
        <Button 
          variant="outlined" 
          color="info"
          startIcon={<Receipt />}
          onClick={() => onExpenditure && onExpenditure(batch)}
        >
          Add Expenditure
        </Button>
        <Button 
          variant="outlined" 
          color="success"
          startIcon={<Sell />}
          onClick={() => onSell && onSell(batch)}
        >
          Sell Batch
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => onEdit && onEdit(batch)}
        >
          Edit Batch
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewBatch;
